<template>
  <f7-card class="avocash-banner" :padding="false">
    <div class="avocash-banner-ambient"></div>

    <f7-card-content class="avocash-banner-content" :padding="false">
      <div class="avocash-banner-header">
        <div class="avocash-banner-info">
          <div class="avocash-banner-badge">
            <span class="avocash-pulse-dot" />
            <span class="avocash-badge-text">{{ $t('common.personalDashboard') }}</span>
          </div>
          <h2 class="avocash-greeting">{{ greetingText }} 👋</h2>
          <p class="avocash-subtext">{{ subtext }}</p>
        </div>

        <f7-badge class="avocash-certified-pill" pill>
          <f7-icon material="shield" :size="18" class="me-1" />
          <span class="avocash-pill-text">{{ $t('common.certifiedBalance') }}</span>
        </f7-badge>
      </div>

      <!-- Micro summary metrics strip -->
      <div v-if="walletBalance" class="avocash-metrics-strip">
        <div class="avocash-metric-col">
          <span class="avocash-metric-label">{{ $t('common.availableBalance') }}</span>
          <span class="avocash-metric-value avocash-metric-value--primary">
            {{ walletBalance.amount.toLocaleString() }} <span class="avocash-currency">{{ walletBalance.currency }}</span>
          </span>
        </div>
        <div class="avocash-metric-col avocash-metric-divider">
          <span class="avocash-metric-label">{{ $t('common.activeGroups') }}</span>
          <span class="avocash-metric-value avocash-metric-value--secondary">
            {{ activeGroupsCount }} <span class="avocash-unit">{{ $t('common.active') }}</span>
          </span>
        </div>
      </div>
    </f7-card-content>
  </f7-card>
</template>

<script setup>
import { computed } from 'vue'
import { useKeycloakStore } from '../../js/pinia-stores/keycloak.js'
import { useAccountStore } from '../../js/pinia-stores/account.js'
import i18n from '../../js/i18n.js'

const { t } = i18n.global

const props = defineProps({
  walletBalance: { type: Object, default: null },
  activeGroupsCount: { type: Number, default: 0 }
})
const keycloakStore = useKeycloakStore()
const accountStore = useAccountStore()

const greetingText = computed(() => {
  const firstName = keycloakStore.profile?.given_name || keycloakStore.user?.given_name || keycloakStore.profile?.name || accountStore.userAccount?.name || t('common.defaultSubscriber')
  return `${t('common.welcomeBack')}, ${firstName}`
})

const subtext = computed(() => {
  return t('common.subtext')
})
</script>

<style scoped>
/* All colors resolve through theme-flipping tokens (defined for :root and
   .dark in app.css) — one rule set serves light and dark mode. */
.avocash-banner {
  margin: 0 8px 16px;
  overflow: hidden;
  border-radius: 16px;
  background: var(--f7-card-bg-color, #fff);
  border: 1px solid var(--f7-list-item-border-color, rgba(111, 122, 113, 0.12));
  box-shadow: 0 2px 8px -2px rgba(26, 107, 66, 0.06), 0 1px 3px rgba(44, 52, 46, 0.06);
  color: var(--f7-on-surface, #161d18);
}
.avocash-banner-ambient {
  position: absolute;
  right: -40px;
  bottom: -40px;
  width: 140px;
  height: 140px;
  background: color-mix(in srgb, var(--f7-theme-color, #1a6b42) 22%, transparent);
  border-radius: 50%;
  filter: blur(28px);
  pointer-events: none;
}
.avocash-banner-content {
  position: relative;
  z-index: 1;
  padding: 16px;
}
.avocash-banner-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.avocash-banner-info {
  flex: 1;
  min-width: 0;
}
.avocash-banner-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.avocash-pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--f7-theme-color, #00512e);
  animation: pulse 2s infinite ease-in-out;
  flex-shrink: 0;
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.2); }
}
.avocash-badge-text {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--f7-on-surface-variant, #6f7a71);
}
.avocash-greeting {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 4px;
}
.avocash-subtext {
  font-size: 13px;
  margin: 0;
  color: var(--f7-on-surface-variant, #3f4941);
}
.avocash-certified-pill {
  padding: 6px 12px;
  white-space: nowrap;
  background: var(--f7-surface-container-low, #edf6ed);
  color: var(--f7-theme-color, #1a6b42);
}
.avocash-pill-text {
  font-size: 11px;
  font-weight: 600;
}
.avocash-metrics-strip {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--f7-surface-container-low, #edf6ed) 60%, transparent);
}
.avocash-metric-col {
  min-width: 0;
}
.avocash-metric-divider {
  border-left: 1px solid var(--f7-list-item-border-color, rgba(111, 122, 113, 0.15));
  padding-left: 14px;
}
.avocash-metric-label {
  display: block;
  font-size: 11px;
  color: var(--f7-on-surface-variant, #3f4941);
}
.avocash-metric-value {
  display: block;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 2px;
}
.avocash-metric-value--primary {
  color: var(--f7-theme-color, #1a6b42);
}
.avocash-metric-value--secondary {
  color: var(--f7-secondary, #755b00);
}
.avocash-currency, .avocash-unit {
  font-size: 12px;
  font-weight: 600;
  color: var(--f7-on-surface-variant, #6f7a71);
}
</style>
